import { useFormContext } from "react-hook-form";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Consultation, HistoryConsultation } from "@/types/consultation";
import { useEffect, useRef, useState } from "react";
import { useGetGoalQuery } from "@/services/consultationApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { workoutGoals } from "@/lib/constants/workout";
import { cn } from "@/lib/utils";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

const GoalStep = ({ action, open }: FormStepProps) => {
  const { watch, setValue } = useFormContext<Consultation>();
  const consultationId = watch("id");
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [description, setDescription] = useState("");

  // Watch prefill signals set by HistoryFilter's Copy Data
  const prefilledGoalValues = watch("_prefilled_goal_values" as any);
  const prefilledGoalText = watch("_prefilled_goal_text" as any);

  useEffect(() => {
    if (Array.isArray(prefilledGoalValues) && prefilledGoalValues.length > 0) {
      const labels = (prefilledGoalValues as string[])
        .map((val) => workoutGoals.find((wg) => wg.value === val)?.label ?? val)
        .filter(Boolean) as string[];
      setSelectedGoals(labels);
      if (prefilledGoalText) setDescription(String(prefilledGoalText));
      // Clear signals so this doesn't re-run
      setValue("_prefilled_goal_values" as any, undefined);
      setValue("_prefilled_goal_text" as any, undefined);
    }
  }, [prefilledGoalValues, prefilledGoalText]);

  const { data: goalData } = useGetGoalQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || !open }
  );

  const dataLoaded = useRef(false);
  const loadedForId = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Re-populate whenever the consultation changes or data arrives fresh
    if (loadedForId.current === Number(consultationId) && dataLoaded.current) return;

    if (goalData?.goals && goalData.goals.length > 0) {
      // All goals share the same goal_text (description) — read from first entry
      const firstGoal = goalData.goals[0];
      setDescription(firstGoal.goal_text ?? "");

      // Collect all goal values across all rows and resolve to labels
      const allValues = goalData.goals.flatMap((g) => g.goals ?? []);
      const labels = allValues
        .map((val) => workoutGoals.find((wg) => wg.value === val)?.label ?? val)
        .filter(Boolean) as string[];
      setSelectedGoals(labels);

      dataLoaded.current = true;
      loadedForId.current = Number(consultationId);
    } else if (goalData) {
      // Consultation exists but no goals saved yet — reset local state
      setSelectedGoals([]);
      setDescription("");
      dataLoaded.current = true;
      loadedForId.current = Number(consultationId);
    }
  }, [goalData, consultationId]);

  // Automatically update the React Hook Form values whenever state changes
  useEffect(() => {
    if (selectedGoals.length > 0) {
      const goalsValues = selectedGoals
        .map((label) => {
          const match = workoutGoals.find((wg) => wg.label === label);
          return match?.value ?? label;
        })
        .filter(Boolean);

      const goalsPayload = [
        {
          goal_text: description,
          goals: goalsValues,
          days_per_week: 0,
          duration_per_day: 0,
        },
      ];
      setValue("goals", goalsPayload as any, { shouldDirty: true, shouldValidate: true });
    } else {
      setValue("goals", [], { shouldDirty: true, shouldValidate: true });
    }
  }, [selectedGoals, description, setValue]);

  const toggleGoal = (label: string) => {
  setSelectedGoals([label]);
  };

  const [historyData, setHistoryData] = useState<HistoryConsultation | null>(null);

  return (
    <div className="space-y-4">
      {/* Previous Goals */}
      {historyData?.goals && historyData.goals.length > 0 && (
        <Card className="p-4 border-[#e2e8f0]">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-sm text-[#1e293b]">Previous Goals</p>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 uppercase">
              Historical View
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {historyData.goals.map((goal, idx) => (
              <span
                key={goal.id || idx}
                className="px-3 py-1 rounded-full text-xs font-medium bg-[#f1f5f9] text-[#475569] border border-[#e2e8f0]"
              >
                {goal.goal_text}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Goals Card */}
      <Card className="p-4">
        <div className="mb-3">
          <p className="font-medium text-sm">Goals</p>
          <p className="text-xs text-gray-500 mt-1">
            Select one or more fitness goals for this member.
          </p>
        </div>
        <Separator className="h-[1px] mb-4" />

        <div className="space-y-5">
          {/* Goal pills from workoutGoals */}
          <div className="space-y-2">
            <Label>
              Select Goals <span className="text-red-500">*</span>
            </Label>
            <div className="flex flex-wrap gap-2 pt-1">
              {workoutGoals.map((goal) => {
                const isSelected = selectedGoals.includes(goal.label);
                return (
                  <button
                    key={goal.value}
                    type="button"
                    onClick={() => toggleGoal(goal.label)}
                    className={cn(
                      "px-4 py-2 rounded-full border text-sm font-medium transition-colors",
                      isSelected
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-gray-600 border-gray-300 hover:border-primary/50"
                    )}
                  >
                    {goal.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Add additional notes or context for these goals..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[80px] resize-none"
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GoalStep;
